import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Gamepad2, CalendarDays, Users, Images, Radio, Newspaper } from "lucide-react";
import { api } from "../../lib/api";
import { fileUrl, mediaSrc } from "./HeroSlides";

const ALBUMS = { engagement: "Engagement", planning: "Planning", rehearsal: "Rehearsal", ceremony: "Ceremony", reception: "Reception", portraits: "Portraits", wedding_day: "Wedding day", guest_upload: "From our guests" };
const on = (w, k) => (w.site_sections || {})[k] !== false;
const Head = ({ script, title, I }) => (
  <div className="mb-10"><p className="font-script text-[var(--gold)] text-4xl flex items-center gap-2">{I && <I className="w-6 h-6" />}{script}</p><h2 className="font-serif font-light text-4xl md:text-5xl">{title}</h2></div>
);

function SocialStrip({ w }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/public/weddings/${w.slug}/social?limit=8`).then((r) => setD(r.data)).catch(() => {}); }, [w.slug]);
  return (
    <section className="section-pad bg-[#0b0b0b] text-white" data-testid="hub-social">
      <div className="container-wide px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div><p className="font-script text-[var(--gold)] text-4xl">Live from the party</p><h2 className="font-serif font-light text-4xl md:text-5xl">Our wedding feed</h2></div>
          <div className="flex gap-2"><Link to={`/site/${w.slug}/social`} className="btn-gold" data-testid="hub-social-open">Open the feed</Link><Link to={`/site/${w.slug}/news`} className="btn-outline-gold !text-white !border-white/40" data-testid="hub-news-open"><Newspaper className="w-4 h-4" /> News</Link></div>
        </div>
        {d?.live?.length > 0 && <Link to={`/site/${w.slug}/social`} className="inline-flex items-center gap-2 bg-[#d41a24] rounded-full px-4 py-2 text-sm mb-6 animate-pulse" data-testid="hub-live-badge"><Radio className="w-4 h-4" /> {d.live.length} guest{d.live.length > 1 ? "s are" : " is"} live now</Link>}
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
          {d?.posts.map((p) => (
            <Link key={p.item_id} to={`/site/${w.slug}/social?p=${p.item_id}`} className="relative shrink-0 w-40 md:w-52 aspect-[9/16] rounded-2xl overflow-hidden snap-start bg-white/5" data-testid={`hub-post-${p.item_id}`}>
              {p.type === "video" ? <video src={fileUrl(p.path)} muted playsInline className="w-full h-full object-cover" /> : <img src={fileUrl(p.path)} alt="" loading="lazy" className="w-full h-full object-cover" />}
              {p.type === "video" && <Play className="absolute top-3 right-3 w-5 h-5" />}
              <span className="absolute bottom-0 inset-x-0 p-3 text-xs bg-gradient-to-t from-black/80 to-transparent line-clamp-2">{p.caption || p.by}</span>
            </Link>
          ))}
          {d && !d.posts.length && <p className="text-white/60">Be the first to post a photo or video!</p>}
        </div>
      </div>
    </section>
  );
}

function Albums({ w }) {
  const pics = (w.gallery || []).filter((m) => m.content_type?.startsWith("image/"));
  const groups = Object.entries(pics.reduce((a, m) => { const k = m.event_category || "wedding_day"; (a[k] = a[k] || []).push(m); return a; }, {}));
  const [tab, setTab] = useState(null);
  if (!pics.length) return null;
  const cur = tab || groups[0][0];
  return (
    <section className="section-pad" data-testid="hub-albums">
      <div className="container-wide px-6">
        <Head script="Memories" title="Our photo albums" I={Images} />
        <div className="flex flex-wrap gap-2 mb-6">{groups.map(([k, v]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-xs border ${cur === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`} data-testid={`album-tab-${k}`}>{ALBUMS[k] || k} · {v.length}</button>)}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{(groups.find(([k]) => k === cur) || [, []])[1].map((m) => <div key={m.item_id} className="aspect-square rounded-2xl overflow-hidden"><img src={fileUrl(m.storage_path)} alt={m.caption || ""} loading="lazy" className="w-full h-full object-cover" /></div>)}</div>
      </div>
    </section>
  );
}

function Schedule({ w }) {
  const [ev, setEv] = useState([]);
  useEffect(() => { api.get(`/public/site/${w.slug}/events?guest=${localStorage.getItem(`lif_guest_${w.slug}`) || ""}`).then((r) => setEv(r.data)).catch(() => {}); }, [w.slug]);
  if (!ev.length) return null;
  return (
    <section className="section-pad bg-[var(--bg-warm)]" data-testid="hub-schedule">
      <div className="container-wide px-6"><Head script="The weekend" title="Schedule of events" I={CalendarDays} />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">{ev.map((e) => <div key={e.event_id} className="bg-white rounded-2xl p-5 border border-[var(--line)]"><p className="text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{e.event_datetime ? new Date(e.event_datetime).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</p><p className="font-serif text-2xl mt-1">{e.name}</p>{e.venue && <p className="text-sm text-[var(--muted)]">{e.venue}</p>}{e.dress_code && <p className="text-xs mt-2">Dress: {e.dress_code}</p>}</div>)}</div>
      </div>
    </section>
  );
}

function VideoNotes({ notes }) {
  if (!notes.length) return null;
  return (
    <div className="mt-14" data-testid="hub-video-notes">
      <p className="font-script text-[var(--gold)] text-3xl mb-4">Video notes</p>
      <div className="flex gap-4 overflow-x-auto pb-2">{notes.map((n) => <div key={n.note_id} className="shrink-0 w-44 rounded-2xl overflow-hidden bg-black relative" data-testid={`hub-video-note-${n.note_id}`}><video src={fileUrl(n.path)} controls playsInline preload="metadata" className="w-full aspect-[9/16] object-cover" /><p className="absolute top-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-b from-black/70 to-transparent">From {n.name}</p></div>)}</div>
    </div>
  );
}

function Party({ w }) {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get(`/public/weddings/${w.slug}/wedding-party`).then((r) => setRows(r.data)).catch(() => {});
    api.get(`/public/weddings/${w.slug}/video-notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [w.slug]);
  if (!rows.length && !notes.length && !w.seating_public) return null;
  return (
    <section className="section-pad" data-testid="hub-party">
      <div className="container-wide px-6"><Head script="Our people" title="Meet the wedding party" I={Users} />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 text-center">{rows.map((p) => <div key={p.name}><div className="w-24 h-24 mx-auto rounded-full bg-[var(--bg-warm)] overflow-hidden flex items-center justify-center font-serif text-3xl text-[var(--gold)]">{p.photo_url ? <img src={mediaSrc(p.photo_url)} alt="" className="w-full h-full object-cover" /> : p.name[0]}</div><p className="font-medium mt-3">{p.name}</p><p className="text-xs uppercase tracking-wider text-[var(--muted)]">{(p.wedding_party_role || "").replace(/_/g, " ")}</p></div>)}</div>
        <VideoNotes notes={notes} />
        <div className="mt-8 text-center"><Link to={`/site/${w.slug}/travel`} className="btn-outline-gold inline-flex" data-testid="hub-travel">Travel & hotels</Link></div>
        {w.seating_public && <div className="mt-12 text-center"><Link to={`/site/${w.slug}/seating`} className="btn-gold inline-flex" data-testid="hub-find-table">Find your table</Link></div>}
      </div>
    </section>
  );
}

function Games({ w }) {
  const [g, setG] = useState([]);
  useEffect(() => { api.get(`/public/weddings/${w.slug}/games`).then((r) => setG(r.data)).catch(() => {}); }, [w.slug]);
  useEffect(() => { if (g.length && window.location.hash === "#games") setTimeout(() => document.getElementById("games")?.scrollIntoView({ behavior: "smooth" }), 300); }, [g.length]);
  if (!g.length) return null;
  return (
    <section id="games" className="section-pad bg-[var(--ink)] text-white" data-testid="hub-games">
      <div className="container-wide px-6"><Head script="Let's play" title="Games & party fun" I={Gamepad2} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{g.map((x) => <Link key={x.code} to={x.href || `/party/${x.code}`} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-[var(--gold)] transition-colors" data-testid={`hub-game-${x.code}`}><p className="text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{x.type}</p><p className="font-serif text-2xl mt-1">{x.title}</p><p className="text-sm text-white/60 mt-2">Tap to join · code {x.code}</p></Link>)}</div>
      </div>
    </section>
  );
}

export function GuestHub({ w }) {
  return (
    <div data-testid="guest-hub">
      {on(w, "social") && <SocialStrip w={w} />}
      {on(w, "schedule") && <Schedule w={w} />}
      {on(w, "games") && <Games w={w} />}
      {on(w, "party") && <Party w={w} />}
      {on(w, "albums") && <Albums w={w} />}
    </div>
  );
}
